import React, { useState, useCallback } from "react";
import {
  Box,
  FormControl,
  Icon,
  Input,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import TextField from "@material-ui/core/TextField";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { isNil, isArray, isEmpty } from "lodash";

import { useAccessesStore } from "../../../../core/stores/AccessesStore";
import { flattenMenuItems } from "../../../../util/UtilFormat";
import { getLocaleText } from "../../../../util/UtilTraduction";
import { useTranslation } from "react-i18next";
import FaIconsGrid from "../faIcons/FaIconsGrid";

//const DEFAULT_MARGIN = 16;
const DEFAULT_MARGIN = 0;

const useStyles = makeStyles((theme) => ({
  dialogCont: {
    minWidth: 800,
    maxWidth: 800,
    minHeight: 400,
    maxHeight: 400,
    display: "flex",
    gap: 10,
  },
  comboPosition: {
    marginTop: DEFAULT_MARGIN,
    minWidth: "100%",
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  buttonsContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  inputsCont: {
    width: 360,
  },
  numberStep: {
    width: "25px",
    height: "25px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: `${theme.palette.content.mainColor}`,
    borderRadius: "25px",
  },
  addBtn: {
    width: 150,
    backgroundColor: `${theme.palette.content.mainColor}`,
    color: "#FFF",
    "&:hover": {
      backgroundColor: `${theme.palette.content.mainColor}`,
      color: "#FFF",
      transform: "scale(1.05)",
    },
  },
}));

export default function AddNewBandDialog({
  initialBandName,
  open,
  handleOpenNewBandDialog,
}) {
  const { t, i18n } = useTranslation();
  const classes = useStyles();
  const [position, setPosition] = useState(1);
  const [newBandName, setNewBandName] = useState(initialBandName || "");
  const [itemsNewBand, setItemsNewBand] = useState([]);
  const [inputItemsNewBand, setInputItemsNewBand] = useState("");
  const [selectedIcon, setSelectedIcon] = useState(null);

  const { addNewBand, menuItems } = useAccessesStore((state) => ({
    addNewBand: state.addNewBand,
    menuItems: state.menuItems,
  }));

  const handleBandName = (event) => {
    setNewBandName(event.target.value);
  };

  const handlePosition = (event) => {
    setPosition(Number(event.target.value));
  };

  const handleChangeSelectedIcon = useCallback((newIcon) => {
    setSelectedIcon(newIcon);
  }, []);

  function handleAddNewBand() {
    addNewBand({
      newBandName,
      position,
      items: itemsNewBand,
      i18n,
      newTitleIcon: selectedIcon,
    });
    handleOpenNewBandDialog(false);
  }

  if (!menuItems || !isArray(menuItems)) {
    return null;
  }

  const flattenedMenuItems = flattenMenuItems(menuItems);

  return (
    <div>
      <Dialog
        open={open}
        onClose={() => handleOpenNewBandDialog(false)}
        aria-labelledby="form-dialog-title"
        maxWidth="lg"
      >
        <DialogTitle id="form-dialog-title">
          <Typography className={classes.dialogTitle}>
            {t("CUSTOM_HOME_ADD_BAND")}
            <div className={classes.buttonsContainer}>
              <Button
                onClick={() => handleAddNewBand()}
                disabled={
                  isNil(newBandName) ||
                  "" === newBandName ||
                  isNil(itemsNewBand) ||
                  isEmpty(itemsNewBand)
                }
                endIcon={<Icon className="fas fa-plus" fontSize="small" />}
                className={classes.addBtn}
              >
                {t("VIEWS_FOR_USER_CREATE_VIEW_CONFIRM")}
              </Button>
              <Button onClick={() => handleOpenNewBandDialog(false)}>
                {t("FORM_CANCEL")}
              </Button>
            </div>
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogCont}>
          <div className={classes.inputsCont}>
            <Typography className={classes.numberStep}>1</Typography>
            <TextField
              autoFocus
              margin="dense"
              id="new-band-name"
              label={t("CUSTOM_HOME_ADD_ITEM_BAND_DIALOG_NB")}
              type="text"
              fullWidth
              value={newBandName}
              onChange={handleBandName}
              disabled={!isNil(initialBandName) && "" !== initialBandName}
              variant="outlined"
            />
            <FormControl className={classes.comboPosition}>
              <InputLabel id="new-band-name-select-position-label">
                {t("CUSTOM_HOME_ADD_POSITION")}
              </InputLabel>
              <Select
                labelId="new-band-name-select-position-label"
                id="new-band-name-select-position"
                value={position}
                onChange={handlePosition}
                input={<Input />}
              >
                <MenuItem value={1}>
                  {t("CUSTOM_HOME_ADD_POSITION_BEGIN")}
                </MenuItem>
                <MenuItem value={2}>
                  {t("CUSTOM_HOME_ADD_POSITION_END")}
                </MenuItem>
              </Select>
            </FormControl>
            <FaIconsGrid handleChangeSelectedIcon={handleChangeSelectedIcon} />
          </div>
          <Box width={"100%"}>
            <Typography className={classes.numberStep}>2</Typography>
            <Autocomplete
              multiple
              id="new-band-name-select-items"
              value={itemsNewBand}
              options={flattenedMenuItems}
              onChange={(event, newValue) => {
                setItemsNewBand(newValue);
              }}
              inputValue={inputItemsNewBand}
              onInputChange={(event, newInputValue) => {
                setInputItemsNewBand(newInputValue);
              }}
              disableCloseOnSelect
              getOptionLabel={(option) => {
                return getLocaleText(option.propText, i18n);
              }}
              renderOption={(option, { selected }) => (
                <>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {getLocaleText(option.propText, i18n)}
                </>
              )}
              style={{ width: "100%", marginTop: DEFAULT_MARGIN }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label={t("CUSTOM_HOME_ADD_ITEMS_LABEL")}
                  placeholder=""
                  fullWidth
                  margin="dense"
                />
              )}
              limitTags={4}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
}
